import {formatCurrency} from "~/utils";
import {Button, Icon} from "~/components";
import QuantitySuggestions from "./QuantitySuggestions";
import KgWeightSelector from "./KgWeightSelector";
import {useRef, useState} from "react";
import '../style/CartItem.css';

const CartItem = ({item, onIncrease, onDecrease, onRemove, onQuantityChange, onPriceChange, units}) =>
{
    // Calculate subtotal based on unit
    // For kg products: price is stored as 100g price, so we need to multiply by selectedWeight to get actual price
    let price = item.price;

    let quantity = item.quantity;

    if (item.unit === 'kg')
    {
        // Display price = 100g price * 10 (to show 1kg price)
        price = item.price * 10;

        quantity = quantity * 10;
    }

    const subtotal = item.price * quantity;

    const vatAmount = (subtotal * (item.vat || 0)) / 100;

    const total = subtotal + vatAmount;

    const inputRef = useRef(null);

    const [isEditingPrice, setIsEditingPrice] = useState(false);
    const [priceInput, setPriceInput] = useState('');
    const [kgRawInput, setKgRawInput] = useState(null);

    const handlePriceClick = () => {
        setIsEditingPrice(true);
        setPriceInput(price.toString());
    };

    const handlePriceInputChange = (e) => {
        const value = e.target.value.replace(/[^\d]/g, '');
        setPriceInput(value);
    };

    const handlePriceBlur = () => {
        setIsEditingPrice(false);
        const numericValue = parseFloat(priceInput) || 0;
        if (numericValue >= 0) {
            // For kg: convert back from 1kg price to 100g price
            const actualPrice = item.unit === 'kg' ? numericValue / 10 : numericValue;
            onPriceChange(item.id, actualPrice);
        }
    };

    const handlePriceKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
        if (e.key === 'Escape') {
            setIsEditingPrice(false);
        }
    };

    const handleQuantityInputChange = (e) =>
    {
        let inputValue = e.target.value;

        // Allow empty string to enable user to clear and type new number
        if (inputValue === '') {
            if (item.unit === 'kg') setKgRawInput('');
            onQuantityChange(item.id, '');
            return;
        }

        // For kg products: use text input with auto-decimal logic
        if (item.unit === 'kg')
        {
            // Only allow digits and one decimal point
            inputValue = inputValue.replace(/[^\d.]/g, '');

            // Auto-insert decimal: if starts with "0" followed by a digit (not a dot), insert "."
            // e.g. "0123" -> "0.123", "01" -> "0.1"
            if (/^0\d/.test(inputValue) && !inputValue.startsWith('0.')) {
                inputValue = '0.' + inputValue.slice(1);
            }

            // Prevent multiple decimal points
            const parts = inputValue.split('.');
            if (parts.length > 2) {
                inputValue = parts[0] + '.' + parts.slice(1).join('');
            }

            setKgRawInput(inputValue);

            const value = parseFloat(inputValue);
            if (!isNaN(value) && value >= 0) {
                onQuantityChange(item.id, value);
            } else if (inputValue === '0.' || inputValue === '0') {
                // Allow intermediate states like "0." while typing
                onQuantityChange(item.id, 0);
            }
            return;
        }

        // For other products: allow only integers
        const value = parseFloat(inputValue);
        if (Number.isInteger(value) && value >= 0) {
            onQuantityChange(item.id, value);
        }
    };

    const handleQuantityBlur = () => {
        // If quantity is empty string when user leaves input, set to 0
        if (item.quantity === '') {
            onQuantityChange(item.id, 0);
        }
        // Reset raw input on blur so it shows the clean numeric value
        if (item.unit === 'kg') {
            setKgRawInput(null);
        }
    };

    // Handle weight button change (100g, 500g, 1kg) for kg products
    // These buttons are shortcuts to quickly set quantity to 0.1, 0.5, or 1
    const handleWeightChange = (weight) => {
        // Set quantity directly to the weight value
        onQuantityChange(item.id, weight);
    };

    return (
        <div className="pos-cart-item">
            <div className="pos-cart-item-header">
                <div className="pos-cart-item-info">
                    <h5 className="pos-cart-item-title">{item.title}</h5>
                    {item.attribute_label && (
                        <span className="pos-cart-item-attribute">{item.attribute_label}</span>
                    )}
                    <div className="pos-cart-item-price-line">
                        {isEditingPrice ? (
                            <input
                                type="text"
                                className="price-edit-input"
                                value={priceInput}
                                onChange={handlePriceInputChange}
                                onBlur={handlePriceBlur}
                                onKeyDown={handlePriceKeyDown}
                                autoFocus
                            />
                        ) : (
                            <span className="price-value price-editable" onClick={handlePriceClick} title="Click để sửa giá">
                                {formatCurrency(price)}
                                <span className="price-edit-icon">✎</span>
                            </span>
                        )}
                        <span className={`stock-value ${item.unit === 'kg' ? 'out-of-stock' : ''}`}>
                            {item.unit === 'kg' ? '1 kg' : units[item.unit]?.label }
                        </span>
                        <span className="vat-badge">VAT {item.vat}%</span>
                    </div>
                </div>
                <Button
                    className="pos-cart-item-remove"
                    red
                    small
                    onClick={() => onRemove(item.id)}
                >
                    {Icon.delete}
                </Button>
            </div>

            <div className="pos-cart-item-footer">
                <div>
                    {item.unit === 'kg' && (
                        <KgWeightSelector
                            selectedWeight={item.quantity}
                            onWeightChange={handleWeightChange}
                        />
                    )}

                    <div className="quantity-controls">
                        <Button
                            small
                            onClick={() => onDecrease(item.id)}
                            disabled={item.quantity <= 0}
                        >-</Button>

                        {/* For kg products: show quantity input as decimal */}
                        {item.unit === 'kg' ? (
                            <input
                                ref={inputRef}
                                type="text"
                                inputMode="decimal"
                                className="quantity-input"
                                value={kgRawInput !== null ? kgRawInput : item.quantity}
                                onChange={handleQuantityInputChange}
                                onFocus={() => setKgRawInput(item.quantity === 0 ? '' : String(item.quantity))}
                                onBlur={handleQuantityBlur}
                                placeholder="0"
                                title="Nhập số lượng kg (VD: 0.6 = 600g, 1.4 = 1kg4)"
                            />
                        ) : (
                            /* For other products: show quantity input with QuantitySuggestions */
                            <QuantitySuggestions
                                value={item.quantity}
                                unit={item.unit}
                                onChange={(value) => onQuantityChange(item.id, value)}
                                units={units}
                            >
                                <input
                                    ref={inputRef}
                                    type="number"
                                    className="quantity-input"
                                    value={item.quantity}
                                    onChange={handleQuantityInputChange}
                                    onBlur={handleQuantityBlur}
                                    min="0"
                                    step="1"
                                    placeholder="0"
                                    title="Click để xem gợi ý"
                                />
                            </QuantitySuggestions>
                        )}

                        <Button
                            small
                            onClick={() => onIncrease(item.id)}
                        >+</Button>
                    </div>
                </div>
                <div className="pos-cart-item-total">
                    <span className="total-value">{formatCurrency(total)}</span>
                </div>
            </div>
        </div>
    );
};

export default CartItem;
